/**
 * Prototyper Facade (100% Shared)
 *
 * Wraps all prototyper-related state and actions.
 * Handles prototype creation, cancellation, and state management.
 *
 * This facade is completely platform-agnostic and can be used
 * by both web and plugin without any modifications.
 *
 * Usage:
 * ```tsx
 * const prototyper = usePrototyperFacade(projectId);
 * await prototyper.createPrototype(conversationId);
 * ```
 *
 * @module usePrototyperFacade
 * @category Facades/Shared
 */

import { useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  setIsCreatingPrototype,
  setIsFetchingPrototype,
  setPrototypeError,
  setPrototypeUrl,
  setCurrentPrototyper,
  updatePrototyperSlug,
  clearPrototyper,
  clearPrototypeError,
  selectPrototyperState,
  selectPrototypeGenerationModel,
  setPrototypeGenerationModel,
} from '../../../store/slices/prototyperSlice';
import {
  updateAIMessage,
  selectConversationMessages,
} from '../../../store/slices/conversationSlice';
import { getUserProfile } from '../../../store/slices/appSlice';
import { useAccessToken } from '../../useAccessToken';
import { prototyperService } from '../../../api/services/prototyper.service';

export interface CurrentPrototyper {
  demo_url: string | null;
  prototyper_demo_link_id: number | null;
  slug: string | null;
  header: string | null;
  isNewlyGenerated?: boolean;
}

export interface PrototyperFacade {
  // ========== State ==========
  /**
   * Whether a prototype is currently being created
   */
  isCreatingPrototype: boolean;

  /**
   * Prototype creation error message
   */
  prototypeError: string | null;

  /**
   * URL of the created prototype
   */
  prototypeUrl: string | null;

  /**
   * Current prototyper data (demo_url and prototyper_demo_link_id)
   */
  currentPrototyper: CurrentPrototyper;

  // ========== Actions ==========
  /**
   * Create a prototype from a conversation
   */
  createPrototype: (conversationId: string) => Promise<void>;

  /**
   * Cancel ongoing prototype creation
   */
  cancelPrototype: () => void;

  /**
   * Set prototype creation state
   */
  setIsCreatingPrototype: (value: boolean) => void;

  /**
   * Set prototype URL
   */
  setPrototypeUrl: (url: string | null) => void;

  /**
   * Handle prototype creation with UI state management
   */
  handlePrototypeCreation: (
    conversationId: string,
    overrideProjectId?: string | null
  ) => Promise<void>;

  /**
   * Clear any prototype errors
   */
  clearError: () => void;

  /**
   * Update prototyper demo link (slug and/or header)
   */
  updatePrototyperDemoLink: (id: number, slug?: string | null) => Promise<void>;

  /**
   * Fetch prototyper by ID
   */
  fetchPrototyperById: (id: string | number) => Promise<void>;

  /**
   * Selected LLM for POST /prototype (not chat llm_family)
   */
  prototypeGenerationModel: { modelProvider: string; modelChoice: string };

  setPrototypeGenerationModel: (model: { modelProvider: string; modelChoice: string }) => void;
}

/**
 * Prototyper Facade Hook
 *
 * Provides prototype management capabilities.
 * 100% shared between web and plugin.
 * Now uses Redux for state management.
 *
 * @param projectId - Optional project ID for project-scoped prototypes
 */
export function usePrototyperFacade(projectId?: string | null): PrototyperFacade {
  // ========== Redux State ==========
  const dispatch = useAppDispatch();
  const prototyperState = useAppSelector(selectPrototyperState);
  const prototypeGenerationModel = useAppSelector(selectPrototypeGenerationModel);
  const messages = useAppSelector(selectConversationMessages);
  const accessToken = useAccessToken();

  const setGenerationModel = useCallback(
    (model: { modelProvider: string; modelChoice: string }) => {
      dispatch(setPrototypeGenerationModel(model));
    },
    [dispatch]
  );

  // Safety check: ensure prototyper state exists
  const safePrototyperState = prototyperState || {
    currentPrototyper: {
      demo_url: null,
      prototyper_demo_link_id: null,
      slug: null,
      header: null,
      isNewlyGenerated: false,
    },
    isCreatingPrototype: false,
    isFetchingPrototype: false,
    prototypeError: null,
    prototypeUrl: null,
  };

  // ========== Refs ==========
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Create a prototype from a conversation
   */
  const createPrototype = useCallback(
    async (conversationId: string) => {
      if (!conversationId) {
        console.error('No conversation ID provided for prototype creation');
        dispatch(setPrototypeError('No conversation ID available'));
        return;
      }

      // Cancel any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      try {
        dispatch(setIsCreatingPrototype(true));

        const response = await prototyperService.createPrototype({
          conversationId,
          projectId,
          modelProvider: prototypeGenerationModel.modelProvider,
          modelChoice: prototypeGenerationModel.modelChoice,
        });

        console.log('Prototype created successfully:', response);

        // Set the prototype URL if available (check both snake_case and camelCase)
        const demoUrl = response.demo_url || response.demoUrl;
        if (demoUrl) {
          dispatch(setPrototypeUrl(demoUrl));
        }

        // Set current prototyper data (handle both naming conventions)
        const prototyperId = response.id || response.prototyper_demo_link_id;
        if (demoUrl || prototyperId) {
          dispatch(
            setCurrentPrototyper({
              demo_url: demoUrl || null,
              prototyper_demo_link_id: prototyperId || null,
              slug: response.slug || null,
              header: response.header || null,
              isNewlyGenerated: response.isNewlyGenerated,
            })
          );
        }

        // Update the last message with prototyper_demo_link_id
        if (prototyperId && messages.length > 0) {
          const lastMessage = messages[messages.length - 1];
          if (lastMessage && lastMessage.role === 'assistant') {
            dispatch(
              updateAIMessage({
                messageId: lastMessage.id,
                content: lastMessage.content || '',
                prototyper_demo_link_id: prototyperId,
              })
            );
          }
        }
      } catch (error) {
        console.error('Error creating prototype:', error);

        let errorMessage = 'Failed to create prototype';
        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            errorMessage = 'Prototype creation cancelled';
          } else {
            errorMessage = error.message || errorMessage;
          }
        }

        dispatch(setPrototypeError(errorMessage));
        throw error;
      } finally {
        dispatch(setIsCreatingPrototype(false));

        // Silent refresh: Update user data (prototype credits may have changed)
        // Fire-and-forget, silently fail if error
        if (accessToken) {
          dispatch(getUserProfile(accessToken)).catch(() => {
            // Silent failure - don't interrupt user flow
          });
        }
      }
    },
    [
      projectId,
      dispatch,
      messages,
      prototypeGenerationModel.modelProvider,
      prototypeGenerationModel.modelChoice,
    ]
  );

  /**
   * Cancel ongoing prototype creation
   */
  const cancelPrototype = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    dispatch(clearPrototyper());
  }, [dispatch]);

  /**
   * Handle prototype creation with full UI state management
   */
  const handlePrototypeCreation = useCallback(
    async (conversationId: string, overrideProjectId?: string | null) => {
      dispatch(setIsCreatingPrototype(true));

      try {
        await createPrototype(conversationId);
      } catch (error) {
        console.error('Prototype creation failed:', error);
      }
    },
    [createPrototype, dispatch]
  );

  /**
   * Clear any prototype errors
   */
  const clearError = useCallback(() => {
    dispatch(clearPrototypeError());
  }, [dispatch]);

  /**
   * Update prototyper demo link (slug and/or header)
   */
  const updatePrototyperDemoLink = useCallback(
    async (id: number, slug?: string | null) => {
      try {
        const response = await prototyperService.updatePrototyperDemoLink({
          id,
          slug,
        });

        console.log('Prototyper demo link updated successfully:', response);

        // Update current prototyper data with new values
        if (slug !== undefined) {
          dispatch(updatePrototyperSlug(slug));
        }
      } catch (error) {
        console.error('Error updating prototyper demo link:', error);

        let errorMessage = 'Failed to update prototyper demo link';
        if (error instanceof Error) {
          errorMessage = error.message || errorMessage;
        }

        dispatch(setPrototypeError(errorMessage));
        throw error;
      }
    },
    [dispatch]
  );

  /**
   * Fetch prototyper by ID and update state
   */
  const fetchPrototyperById = useCallback(
    async (id: string | number) => {
      try {
        dispatch(setIsFetchingPrototype(true));

        const response = await prototyperService.getPrototyperById(id);

        console.log('Prototyper fetched successfully:', response);

        // Set the prototype URL if available
        const demoUrl = response.demo_url || response.demoUrl;
        if (demoUrl) {
          dispatch(setPrototypeUrl(demoUrl));
        }

        // Set current prototyper data
        const prototyperId = response.id || response.prototyper_demo_link_id;
        if (demoUrl || prototyperId) {
          dispatch(
            setCurrentPrototyper({
              demo_url: demoUrl || null,
              prototyper_demo_link_id: prototyperId || null,
              slug: response.slug || null,
              header: response.header || null,
            })
          );
        }
      } catch (error) {
        console.error('Error fetching prototyper:', error);

        let errorMessage = 'Failed to fetch prototyper';
        if (error instanceof Error) {
          errorMessage = error.message || errorMessage;
        }

        dispatch(setPrototypeError(errorMessage));
        throw error;
      } finally {
        dispatch(setIsFetchingPrototype(false));
      }
    },
    [dispatch]
  );

  return {
    // State from Redux
    isCreatingPrototype: safePrototyperState.isCreatingPrototype,
    prototypeError: safePrototyperState.prototypeError,
    prototypeUrl: safePrototyperState.prototypeUrl,
    currentPrototyper: safePrototyperState.currentPrototyper,

    // Actions
    createPrototype,
    cancelPrototype,
    setIsCreatingPrototype: useCallback(
      (value: boolean) => dispatch(setIsCreatingPrototype(value)),
      [dispatch]
    ),
    setPrototypeUrl: useCallback(
      (url: string | null) => dispatch(setPrototypeUrl(url)),
      [dispatch]
    ),
    handlePrototypeCreation,
    clearError,
    updatePrototyperDemoLink,
    fetchPrototyperById,
    prototypeGenerationModel,
    setPrototypeGenerationModel: setGenerationModel,
  };
}
